import { useState, useEffect, useCallback } from "react";
import format from "date-fns/format";

/**
 * Objective of this hook is to manage the state and functions for filtering orders.
 * It handles the fetching of data and CSV data based on the search criteria.
 * It also manages the loading state and the visibility of columns in the table.
 * The hook is designed to be reusable and can be used in different modules (purchase, shipping, refund).
 * @param {Object} initialState - Initial state for the filter
 * @param {Object} initialVisibilityModel - Initial visibility model for the columns
 * @param {Function} onFetch - Function to fetch data
 * @param {Function} onFetchCsv - Function to fetch CSV data
 * @param {boolean} isSmallerThanTablet - Flag to check if the screen size is smaller than tablet
 * @param {Array} dependencies - Dependencies for the useEffect hook when fetching data
 * @param {string} module - Module name to handle different API responses (purchase, shipping, refund)
 * @returns {Object} - Returns the state and functions for filtering orders
 */

const useFilterOrder = ({
  initialState = {},
  initialVisibilityModel = {},
  onFetch = () => {},
  onFetchCsv = () => {},
  isSmallerThanTablet = false,
  dependencies = [],
  module = "purchase",
}) => {
  const [dataSearch, setDataSearch] = useState(initialState);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initialVisibilityModel,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCsv, setIsLoadingCsv] = useState(true);
  const [dataList, setDataList] = useState([]);
  const [dataCsv, setDataCsv] = useState([]);
  const [statusCount, setStatusCount] = useState({});
  const [rowCount, setRowCount] = useState(0);

  const dependenciesArray = dependencies.map((item) => {
    const value = dataSearch[item];
    return typeof value === "object" ? JSON.stringify(value) : value;
  });

  const formatPayload = (searchData) => {
    if (module === "purchase" || module === "refund") {
      // ถ้า module เป็น purchase หรือ refund จะทำการ format date
      return {
        ...searchData,
        date: searchData.date ? format(searchData.date, "yyyy-MM-dd") : null,
      };
    }
    // ถ้า module เป็น shipping จะไม่ทำการ format date เพราะ api format date ให้แล้ว
    return { ...searchData };
  };

  const fetchData = async (dataSearch) => {
    try {
      setIsLoading(true);
      const payload = formatPayload(dataSearch);
      const {
        data: { data },
      } = await onFetch(payload);

      if (module === "purchase" || module === "refund") {
        // handle purchase and refund module api response
        setDataList(data?.rows);
        setStatusCount(data?.statusCounts);
        setRowCount(data?.count);
      } else if (module === "shipping") {
        // handle shipping module api response
        setDataList(
          data?.results?.map((item) => {
            return {
              ...item.order,
              status: item?.status?.value,
              shipping_id: item?._id,
              fulfillmentProcess: item?.fulfillmentProcess,
            };
          }),
        );
        setStatusCount(data?.statusCounts);
        setRowCount(data?.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCsvData = async (dataSearch) => {
    if (module === "refund") return;

    try {
      setIsLoadingCsv(true);
      const payload = formatPayload(dataSearch);

      const {
        data: { data },
      } = await onFetchCsv(payload);
      setDataCsv(data);
    } catch (error) {
      console.error("Error fetching CSV data:", error);
      if (error?.response?.status === 404) {
        setDataCsv([]);
      }
    } finally {
      setIsLoadingCsv(false);
    }
  };

  const onSearchButton = async () => {
    try {
      setDataSearch((prev) => ({ ...prev, page: 0 }));
      await fetchData(dataSearch);
      await fetchCsvData(dataSearch);
    } catch (error) {
      console.error("Error searching refund orders:", error);
    }
  };

  const handlePageChange = useCallback(
    (newPage) => {
      setDataSearch((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSearch.page],
  );

  const handleFilterDialog = (value) => {
    setDataSearch((prev) => ({
      ...prev,
      ...value,
      page: 0,
    }));
  };

  useEffect(() => {
    if (dataSearch.page === 0) {
      fetchCsvData(dataSearch);
    }
    fetchData(dataSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependenciesArray]);

  useEffect(() => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      ...initialVisibilityModel,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallerThanTablet]);

  return {
    dataSearch,
    setDataSearch,
    dataList,
    isLoading,
    isLoadingCsv,
    statusCount,
    rowCount,
    onSearchButton,
    handlePageChange,
    isSmallerThanTablet,
    columnVisibilityModel,
    handleFilterDialog,
    dataCsv,
  };
};

export default useFilterOrder;
